import BASE_URL from "../../config/api";
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import ImageSkeleton from "../Houses/ImageSkeleton";
import axios from "axios";
import { BsSearch, BsChevronLeft, BsChevronRight } from "react-icons/bs";

const LIMIT = 20;

const AllFeatured = () => {
  const [loading, setLoading] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Debounce search — 400ms, resets page to 1
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Fetch whenever page or debouncedSearch changes
  const fetchDrawings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      const res = await axios.get(`${BASE_URL}/drawing/get-drawings?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` },
      });
      setDrawings(res.data.data ?? []);
      setTotal(res.data.pagination?.total ?? 0);
      setTotalPages(res.data.pagination?.totalPages ?? 1);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchDrawings();
  }, [fetchDrawings]);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  const pageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (page >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  };

  return (
    <div className="font-Montserrat min-h-screen bg-gray-50">
      <div className="h-[56px] md:h-[72px]" />

      {/* Page header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-10 md:py-16">
          <div className="text-center mb-8">
            <p className="text-[#1877F2] uppercase tracking-[0.3em] text-xs font-semibold mb-3">
              Browse &amp; Buy
            </p>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              All House Plans
            </h1>
            <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">
              Professional architectural designs — ready to purchase and build anywhere in Nigeria.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <BsSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or reference number..."
              className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-200 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#1877F2] transition-colors shadow-sm"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 md:px-10 py-10">
        {loading ? (
          <ImageSkeleton />
        ) : (
          <>
            {/* Result count */}
            {total > 0 && (
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-700">
                    {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-700">{total}</span> plans
                </p>
                {debouncedSearch && (
                  <button
                    onClick={() => setSearch("")}
                    className="text-[#1877F2] hover:underline text-xs font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}

            {/* Empty state */}
            {drawings.length === 0 ? (
              <div className="text-center py-24 text-gray-400">
                <p className="text-4xl mb-4">🏠</p>
                <p className="text-lg font-semibold mb-1">
                  {debouncedSearch ? "No plans match your search" : "No plans available yet"}
                </p>
                {debouncedSearch && (
                  <button
                    onClick={() => setSearch("")}
                    className="mt-4 text-[#1877F2] underline text-sm font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Cards grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {drawings.map((drawing, index) => (
                    <Link
                      key={index}
                      to={`/house-details/${drawing._id}`}
                      className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col"
                    >
                      <div className="relative overflow-hidden h-56 md:h-64 flex-shrink-0">
                        <img
                          src={drawing.frontElevation}
                          alt={drawing.description || "House plan"}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#1877F2] text-white text-sm font-bold px-6 py-2.5 rounded-full">
                            View Plan →
                          </span>
                        </div>
                        <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#1877F2] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-sm">
                          {drawing.refNo}
                        </span>
                      </div>

                      <div className="p-5 flex flex-col flex-1">
                        <h3 className="font-bold text-gray-900 text-sm md:text-base uppercase leading-snug line-clamp-2 flex-1">
                          {drawing.description}
                        </h3>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Price</p>
                            <p className="text-lg font-bold text-[#1877F2]">
                              ₦{(Number((drawing.price || "").toString().replace(/[^0-9.]/g, "")) || 0).toLocaleString()}
                            </p>
                          </div>
                          <span className="text-xs font-semibold text-[#1877F2] border border-[#1877F2] rounded-full px-4 py-1.5 group-hover:bg-[#1877F2] group-hover:text-white transition-colors duration-200">
                            View Plan
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-12">
                    <button
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page === 1}
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#1877F2] hover:text-[#1877F2] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <BsChevronLeft className="text-xs" />
                    </button>

                    {pageNumbers().map((p, i) =>
                      p === "..." ? (
                        <span key={`e-${i}`} className="w-9 h-9 flex items-center justify-center text-gray-400 text-sm">
                          …
                        </span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                            page === p
                              ? "bg-[#1877F2] text-white shadow-sm"
                              : "border border-gray-200 text-gray-600 hover:border-[#1877F2] hover:text-[#1877F2]"
                          }`}
                        >
                          {p}
                        </button>
                      )
                    )}

                    <button
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page === totalPages}
                      className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#1877F2] hover:text-[#1877F2] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <BsChevronRight className="text-xs" />
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AllFeatured;
